import { LoadingWrapper } from '@components';
import { Box, Button, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useBreakPoinst from '@hooks/useBreakPoinst';

import AutomationChat from '@assets/svg/Automation.chat.svg?react';
import AutomationInboxOut from '@assets/svg/Automation.InboxOut.svg?react';
import AutomationChatSquareCall from '@assets/svg/Automation.ChatSquareCall.svg?react';

import StatsCards from '../components/StatsCards';
import AutomationTabs from '../components/AutomationTabs';
import TasksTable from '../components/tasks/TasksTable';
import DeleteTaskDialog from '../components/tasks/DeleteTaskDialog';
import CreateTaskModal from '../components/tasks/CreateTaskModal';
import EditTaskModal from '../components/tasks/EditTaskModal';
import { useTasksAutomation } from '../hooks/useTasksAutomation';
import { useAutomation } from '../hooks/useAutomation';

// Existing messages components (still rendered from old location during migration)
import AutomationStep2 from '../../../pages/Automation/components/AutomationTable';
import AutomationStep1 from '../../../pages/Automation/onboarding/AutomationOnboarding';
import EmptyState from '../../../pages/Automation/components/EmptyState';

const AutomationView = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const { isPhone } = useBreakPoinst();
  const isRtl = language === 'ar';

  const [activeTab, setActiveTab] = useState(0);

  // --- Messages automation state (existing logic preserved) ---
  const [step, setStep] = useState<{
    showOnBoarding: boolean;
    showTable: boolean;
  }>({
    showOnBoarding: false,
    showTable: false,
  });

  // Stats from AutomationStep2
  const [messageStats, setMessageStats] = useState({
    activeMasseges: 0,
    sentMasseges: 0,
    activatedSendingChannels: 0,
  });

  const handleStatsReady = useCallback(
    (stats: {
      activeMasseges: number;
      sentMasseges: number;
      activatedSendingChannels: number;
    }) => {
      setMessageStats(stats);
    },
    []
  );

  const { GetOnboardingQuery, GetListingManagmentQuery } = useAutomation();
  const {
    data: onboardingData,
    isLoading: onboardingLoading,
    refetch: refetchOnboarding,
    isRefetching,
  } = GetOnboardingQuery();
  const { data: listingData, isLoading: listLoading } =
    GetListingManagmentQuery({ pageSize: 5, pageNumber: 1 });

  useEffect(() => {
    refetchOnboarding();
  }, []);

  useEffect(() => {
    if (
      onboardingData &&
      onboardingData?.result &&
      onboardingData?.result?.isOnBoardingComplete
    ) {
      setStep({ showOnBoarding: false, showTable: true });
    } else {
      setStep({ showOnBoarding: true, showTable: false });
    }
  }, [onboardingData]);

  // --- Tasks automation state ---
  const tasksAutomation = useTasksAutomation();

  // --- Stats cards data (single source of truth) ---
  const statsCards = [
    {
      icon: <AutomationChat style={{ width: 32, height: 32 }} />,
      label: t('Automation.EnabledMessages'),
      value: String(messageStats.activeMasseges ?? 0),
    },
    {
      icon: <AutomationInboxOut style={{ width: 32, height: 32 }} />,
      label: t('Automation.SentMessages'),
      value: String(messageStats.sentMasseges ?? 0),
    },
    {
      icon: (
        <AutomationChatSquareCall style={{ width: 32, height: 32 }} />
      ),
      label: t('Automation.activeTasks'),
      value: String(
        tasksAutomation.tasks.filter((task) => task.isActive).length
      ).padStart(2, '0'),
    },
  ];

  // --- Loading state ---
  if (listLoading || onboardingLoading || isRefetching) {
    return (
      <LoadingWrapper loading>
        <div style={{ height: '100vh' }} />
      </LoadingWrapper>
    );
  }

  // --- Empty state (no listings) ---
  if (listingData?.result?.length === 0 && !listLoading) {
    return <EmptyState />;
  }

  return (
    <LoadingWrapper loading={onboardingLoading}>
      <Grid container spacing={2}>
        {/* Page Header */}
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: isPhone ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isPhone ? 'flex-start' : 'center',
              gap: '8px',
              direction: isRtl ? 'rtl' : 'ltr',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: 'Tajawal, sans-serif',
                  fontWeight: 700,
                  fontSize: isPhone ? '20px' : '24px',
                  color: '#130080',
                }}
              >
                {t('Automation.AutomationCenter')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Tajawal, sans-serif',
                  fontWeight: 400,
                  fontSize: isPhone ? '12px' : '16px',
                  color: '#ADBAC7',
                  pt: '4px',
                }}
              >
                {t('Automation.ManageAllAutomationOperations')}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Stats Cards */}
        <Grid item xs={12}>
          <StatsCards cards={statsCards} />
        </Grid>

        {/* Automation Templates Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              border: '1px solid #EAEDF1',
              padding: '24px',
              direction: isRtl ? 'rtl' : 'ltr',
            }}
          >
            {/* Section Title + Tabs */}
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Tajawal, sans-serif',
                    fontWeight: 700,
                    fontSize: '20px',
                    color: '#37352F',
                  }}
                >
                  {t('Automation.automationTemplates')}
                </Typography>
                {activeTab === 1 && (
                  <Button
                    variant="contained"
                    onClick={tasksAutomation.openCreateModal}
                    sx={{
                      backgroundColor: '#5B3FFF',
                      borderRadius: '12px',
                      fontFamily: 'Tajawal, sans-serif',
                      fontSize: '14px',
                      fontWeight: 700,
                      textTransform: 'none',
                      px: 3,
                      py: 1,
                      '&:hover': {
                        backgroundColor: '#4930CC',
                      },
                    }}
                  >
                    {t('Automation.createNewTask')}
                  </Button>
                )}
              </Box>
              <AutomationTabs
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
            </Box>

            {/* Tab Content */}
            {activeTab === 0 && (
              <>
                {/* Messages Automation - preserve existing behavior */}
                <section
                  style={{
                    display: step.showOnBoarding ? 'block' : 'none',
                  }}
                >
                  <AutomationStep1
                    state={() => {
                      setStep({ showOnBoarding: false, showTable: true });
                    }}
                  />
                </section>
                <section
                  style={{
                    display: step.showTable ? 'block' : 'none',
                  }}
                >
                  <AutomationStep2
                    tableOnly
                    onStatsReady={handleStatsReady}
                  />
                </section>
              </>
            )}

            {activeTab === 1 && (
              <>
                {/* Tasks Automation */}
                <TasksTable
                  tasks={tasksAutomation.tasks}
                  totalRecords={tasksAutomation.totalRecords}
                  isLoading={tasksAutomation.tasksLoading}
                  isError={tasksAutomation.tasksError}
                  activePage={tasksAutomation.activePage}
                  perPage={tasksAutomation.perPage}
                  onPageChange={tasksAutomation.setActivePage}
                  onPerPageChange={tasksAutomation.setPerPage}
                  onEdit={tasksAutomation.openEditModal}
                  onDelete={tasksAutomation.openDeleteDialog}
                  onCreate={tasksAutomation.openCreateModal}
                  onRetry={() => tasksAutomation.refetchTasks()}
                />

                <DeleteTaskDialog
                  open={tasksAutomation.deleteDialogOpen}
                  onClose={tasksAutomation.closeDeleteDialog}
                  onConfirm={tasksAutomation.handleDeleteTask}
                  task={tasksAutomation.selectedTask}
                  isDeleting={tasksAutomation.isDeleting}
                />

                <CreateTaskModal
                  open={tasksAutomation.createModalOpen}
                  onClose={tasksAutomation.closeCreateModal}
                  onSuccess={() => {
                    tasksAutomation.refetchTasks();
                  }}
                />

                <EditTaskModal
                  open={tasksAutomation.editModalOpen}
                  task={tasksAutomation.selectedTask}
                  onClose={tasksAutomation.closeEditModal}
                  onSuccess={() => {
                    tasksAutomation.refetchTasks();
                  }}
                />
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default AutomationView;
